import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { UserProfileManager } from '../utils/userProfile'
import { getTicTacToeAIMove } from '../utils/ai/tictactoe'
import { AIDifficulty } from '../components/AIModeSelector'
import { GameHeader } from '../components/GameHeader'
import { GameContext } from '../components/GameContext'
import { GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/GamePage.css'
import '../styles/TicTacToe.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

type Player = 'X' | 'O' | null
type MiniBoard = Player[]
type GameBoard = MiniBoard[]
type GameMode = 'select' | 'local' | 'network' | 'ai'
type GamePhase = 'setup' | 'lobby' | 'waiting' | 'playing' | 'finished'

type Notification = {
  message: string
  type: 'info' | 'success' | 'error'
}

export default function TicTacToe() {
  const [searchParams] = useSearchParams()
  const joinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('setup')

  // AI state
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium')

  // Local game state
  const [player1Name, setPlayer1Name] = useState(userProfile?.name || 'Player 1')
  const [player2Name, setPlayer2Name] = useState('Player 2')
  const [currentPlayer, setCurrentPlayer] = useState<'X' | 'O'>('X')
  const [board, setBoard] = useState<GameBoard>(Array(9).fill(null).map(() => Array(9).fill(null)))
  const [miniWinners, setMiniWinners] = useState<Player[]>(Array(9).fill(null))
  const [nextBoard, setNextBoard] = useState<number | null>(null)
  const [winner, setWinner] = useState<Player>(null)

  // Network game state
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [mySymbol, setMySymbol] = useState<'X' | 'O'>('X')
  const [sessionCode, setSessionCode] = useState<string | undefined>(undefined)

  // Notification state
  const [notification, setNotification] = useState<Notification | null>(null)

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  const {
    session,
    loading,
    error,
    createSession,
    joinSession,
    sendMove,
    voice,
    onMessage,
  } = useEnhancedGameSession({
    sessionCode,
    playerNumber,
    playerName,
  })

  // Auto-join if join code in URL
  useEffect(() => {
    if (joinCode && gameMode === 'select') {
      setGameMode('network')
      setPhase('lobby')
    }
  }, [joinCode, gameMode])

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  const showNotification = (message: string, type: Notification['type'] = 'info') => {
    setNotification({ message, type })
  }

  const handleModeSelect = (mode: GameMode) => {
    setGameMode(mode)
    if (mode === 'local' || mode === 'ai') {
      setPhase('setup')
    } else {
      setPhase('lobby')
    }
  }

  const startLocalGame = () => {
    resetGame()
    setPhase('playing')
  }

  const resetGame = () => {
    setBoard(Array(9).fill(null).map(() => Array(9).fill(null)))
    setMiniWinners(Array(9).fill(null))
    setNextBoard(null)
    setCurrentPlayer('X')
    setWinner(null)
  }

  const checkWinner = (cells: Player[]): Player => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6] // diagonals
    ]

    for (const [a, b, c] of lines) {
      if (cells[a] && cells[a] === cells[b] && cells[a] === cells[c]) {
        return cells[a]
      }
    }
    return null
  }

  const isBoardFull = (cells: Player[]): boolean => {
    return cells.every(cell => cell !== null)
  }

  const handleCellClick = (boardIndex: number, cellIndex: number) => {
    if (gameMode === 'local' || gameMode === 'ai') {
      handleLocalMove(boardIndex, cellIndex)
    } else {
      handleNetworkMove(boardIndex, cellIndex)
    }
  }

  const handleLocalMove = (boardIndex: number, cellIndex: number) => {
    // Check if move is valid
    if (winner) return
    if (miniWinners[boardIndex]) return
    if (board[boardIndex][cellIndex]) return
    if (nextBoard !== null && nextBoard !== boardIndex) return

    // Make the move
    const newBoard = board.map((mini, i) =>
      i === boardIndex ? mini.map((cell, j) => j === cellIndex ? currentPlayer : cell) : mini
    )
    setBoard(newBoard)

    // Check if mini board is won
    const newMiniWinners = [...miniWinners]
    const miniWinner = checkWinner(newBoard[boardIndex])
    if (miniWinner) {
      newMiniWinners[boardIndex] = miniWinner
      setMiniWinners(newMiniWinners)

      // Check if game is won
      const gameWinner = checkWinner(newMiniWinners)
      if (gameWinner) {
        setWinner(gameWinner)
        setPhase('finished')
        const winnerName = gameMode === 'ai'
          ? (gameWinner === 'X' ? player1Name : 'AI')
          : (gameWinner === 'X' ? player1Name : player2Name)
        showNotification(`${winnerName} wins the game!`, 'success')
        return
      }
    }

    // Set next board (fixed: use newMiniWinners, not old miniWinners)
    const nextBoardIndex = (newMiniWinners[cellIndex] !== null || isBoardFull(newBoard[cellIndex])) ? null : cellIndex
    setNextBoard(nextBoardIndex)

    // Switch player
    setCurrentPlayer(currentPlayer === 'X' ? 'O' : 'X')
  }

  const handleNetworkMove = async (boardIndex: number, cellIndex: number) => {
    if (!session) return
    if (winner) return
    if (miniWinners[boardIndex]) return
    if (board[boardIndex][cellIndex]) return
    if (nextBoard !== null && nextBoard !== boardIndex) return
    if ((currentPlayer === 'X' && mySymbol !== 'X') || (currentPlayer === 'O' && mySymbol !== 'O')) {
      showNotification("It's not your turn!", 'error')
      return
    }

    // Make the move
    const newBoard = board.map((mini, i) =>
      i === boardIndex ? mini.map((cell, j) => j === cellIndex ? currentPlayer : cell) : mini
    )
    setBoard(newBoard)

    // Check if mini board is won
    const newMiniWinners = [...miniWinners]
    const miniWinner = checkWinner(newBoard[boardIndex])
    if (miniWinner) {
      newMiniWinners[boardIndex] = miniWinner
      setMiniWinners(newMiniWinners)

      // Check if game is won
      const gameWinner = checkWinner(newMiniWinners)
      if (gameWinner) {
        setWinner(gameWinner)
        setPhase('finished')
        await sendMove(
          { type: 'move', boardIndex, cellIndex },
          { board: newBoard, miniWinners: newMiniWinners, winner: gameWinner, currentPlayer: currentPlayer === 'X' ? 'O' : 'X', nextBoard: null }
        )
        return
      }
    }

    // Set next board (fixed: use newMiniWinners, not old miniWinners)
    const nextBoardIndex = (newMiniWinners[cellIndex] !== null || isBoardFull(newBoard[cellIndex])) ? null : cellIndex
    setNextBoard(nextBoardIndex)

    // Submit move via WebRTC or database
    await sendMove(
      { type: 'move', boardIndex, cellIndex },
      { board: newBoard, miniWinners: newMiniWinners, currentPlayer: currentPlayer === 'X' ? 'O' : 'X', nextBoard: nextBoardIndex }
    )

    // Switch player
    setCurrentPlayer(currentPlayer === 'X' ? 'O' : 'X')
  }

  const handleCreateNetworkGame = async () => {
    if (!playerName.trim()) {
      showNotification('Please enter your nickname', 'error')
      return
    }

    try {
      const newSession = await createSession('tictactoe', playerName.trim())
      setSessionCode(newSession.session_code)
      setPlayerNumber(1)
      setMySymbol('X')
      resetGame()
      setPhase('waiting')
      showNotification(`Game created! Share code: ${newSession.session_code}`, 'success')
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to create game', 'error')
    }
  }

  const handleJoinNetworkGame = async (code?: string) => {
    const targetCode = code || joinCode
    if (!targetCode) {
      showNotification('Please enter a session code', 'error')
      return
    }

    if (!playerName.trim()) {
      showNotification('Please enter your nickname', 'error')
      return
    }

    try {
      await joinSession(targetCode, playerName.trim())
      setSessionCode(targetCode)
      setPlayerNumber(2)
      setMySymbol('O')
      resetGame()
      setPhase('playing')
      showNotification('Joined game successfully!', 'success')
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to join game', 'error')
    }
  }

  // Watch for player 2 joining
  useEffect(() => {
    if (session && phase === 'waiting' && session.player2_name) {
      setPhase('playing')
      showNotification(`${session.player2_name} joined!`, 'success')
    }
  }, [session, phase])

  // Sync game state from network
  useEffect(() => {
    if (session?.game_state && gameMode === 'network') {
      const state = session.game_state
      if (state.board) setBoard(state.board)
      if (state.miniWinners) setMiniWinners(state.miniWinners)
      if (state.currentPlayer) setCurrentPlayer(state.currentPlayer)
      if (state.nextBoard !== undefined) setNextBoard(state.nextBoard)
      if (state.winner) {
        setWinner(state.winner)
        setPhase('finished')
      }
    }
  }, [session?.game_state, gameMode])

  // AI move logic
  useEffect(() => {
    if (gameMode !== 'ai') return
    if (phase !== 'playing') return
    if (currentPlayer !== 'O') return // AI is always 'O'
    if (winner) return

    // Delay AI move slightly for better UX
    const timer = setTimeout(() => {
      const aiMove = getTicTacToeAIMove(board, miniWinners, nextBoard, 'O', aiDifficulty)
      handleLocalMove(aiMove.boardIndex, aiMove.cellIndex)
    }, 500)

    return () => clearTimeout(timer)
  }, [gameMode, phase, currentPlayer, winner, board, miniWinners, nextBoard, aiDifficulty])

  // Handle real-time chat messages for network mode
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribeChat = onMessage('chat', (data) => {
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  // Handler for sidebar message sending (network mode)
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode !== 'network' || !sessionCode) return

    // Send chat message via session
    await sendMove({ type: 'chat', message })

    // Add to local messages
    const newMessage: Message = {
      sender: 'you',
      content: message,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [gameMode, sessionCode, sendMove])

  const NotificationComponent = () => {
    if (!notification) return null

    return (
      <div className={`notification notification-${notification.type}`}>
        {notification.message}
        <button className="notification-close" onClick={() => setNotification(null)}>×</button>
      </div>
    )
  }

  const renderMiniBoard = (boardIndex: number) => {
    const miniBoard = board[boardIndex]
    const miniWinner = miniWinners[boardIndex]
    const isActive = nextBoard === null || nextBoard === boardIndex
    const isClickable = !winner && !miniWinner && isActive

    return (
      <div
        className={`mini-board ${isClickable ? 'active' : ''} ${miniWinner ? 'won' : ''}`}
        key={boardIndex}
      >
        {miniWinner ? (
          <div className={`mini-winner ${miniWinner === 'X' ? 'x-win' : 'o-win'}`}>
            {miniWinner}
          </div>
        ) : (
          <div className="mini-grid">
            {miniBoard.map((cell, cellIndex) => (
              <button
                key={cellIndex}
                className={`cell ${cell ? cell.toLowerCase() : ''}`}
                onClick={() => handleCellClick(boardIndex, cellIndex)}
                disabled={!isClickable || !!cell}
              >
                {cell}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Mode selection screen
  if (gameMode === 'select') {
    return (
      <div className="tictactoe-container">
        <Link to="/" className="back-button">← Back to Games</Link>
        <NotificationComponent />
        <div className="game-setup">
          <h1>⭕❌ Ultimate Tic-Tac-Toe</h1>
          <p>Choose your game mode</p>

          <div className="mode-selection">
            <button onClick={() => handleModeSelect('local')} className="mode-button">
              <h3>🏠 Local Play</h3>
              <p>Play on the same device</p>
            </button>
            <button onClick={() => handleModeSelect('network')} className="mode-button">
              <h3>🌐 Online Play</h3>
              <p>Play with a friend remotely</p>
            </button>
            <button onClick={() => handleModeSelect('ai')} className="mode-button">
              <h3>🤖 Play vs AI</h3>
              <p>Challenge the computer</p>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Local/AI game setup
  if ((gameMode === 'local' || gameMode === 'ai') && phase === 'setup') {
    return (
      <div className="tictactoe-container">
        <Link to="/" className="back-button">← Back to Games</Link>
        <NotificationComponent />
        <div className="game-setup">
          <h1>⭕❌ Ultimate Tic-Tac-Toe - {gameMode === 'ai' ? 'AI' : 'Local'}</h1>

          <div className="setup-section">
            <h3>{gameMode === 'ai' ? 'Your Name' : 'Player Names'}</h3>
            <div className="player-inputs">
              <input
                type="text"
                placeholder="Player 1 (X)"
                value={player1Name}
                onChange={(e) => setPlayer1Name(e.target.value)}
                className="player-name-input"
              />
              {gameMode === 'local' && (
                <input
                  type="text"
                  placeholder="Player 2 (O)"
                  value={player2Name}
                  onChange={(e) => setPlayer2Name(e.target.value)}
                  className="player-name-input"
                />
              )}
            </div>
          </div>

          {gameMode === 'ai' && (
            <div className="setup-section">
              <h3>AI Difficulty</h3>
              <div className="difficulty-buttons">
                <button
                  className={`difficulty-btn ${aiDifficulty === 'easy' ? 'active' : ''}`}
                  onClick={() => setAiDifficulty('easy')}
                >
                  😊 Easy
                </button>
                <button
                  className={`difficulty-btn ${aiDifficulty === 'medium' ? 'active' : ''}`}
                  onClick={() => setAiDifficulty('medium')}
                >
                  😐 Medium
                </button>
                <button
                  className={`difficulty-btn ${aiDifficulty === 'hard' ? 'active' : ''}`}
                  onClick={() => setAiDifficulty('hard')}
                >
                  😤 Hard
                </button>
              </div>
            </div>
          )}

          <div className="setup-section">
            <h3>How to Play</h3>
            <ul className="rules-list">
              <li>Win 3 mini-boards in a row to win the game</li>
              <li>Your move determines which board your opponent plays on next</li>
              <li>If sent to a won board, you can play anywhere</li>
            </ul>
          </div>

          <div className="setup-actions">
            <button onClick={() => setGameMode('select')} className="back-btn">
              Back
            </button>
            <button onClick={startLocalGame} className="start-button">
              Start Game
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Network lobby
  if (gameMode === 'network' && phase === 'lobby') {
    return (
      <div className="tictactoe-container lobby-container">
        <Link to="/" className="back-button">← Back to Games</Link>
        <NotificationComponent />

        <div className="lobby-content">
          <div className="lobby-header">
            <h1>⭕❌ Ultimate Tic-Tac-Toe - Online</h1>
            <p className="lobby-subtitle">Create or join a game!</p>
          </div>

          <div className="lobby-card">
            <div className="input-group">
              <label htmlFor="nickname-input">Your Nickname</label>
              <input
                id="nickname-input"
                type="text"
                placeholder="Enter your nickname"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                className="player-name-input"
                maxLength={20}
              />
            </div>

            <div className="action-buttons">
              <button
                onClick={handleCreateNetworkGame}
                disabled={loading || !playerName.trim()}
                className="primary-button create-btn"
              >
                {loading ? '⏳ Creating...' : '🎮 Create Game'}
              </button>

              <div className="divider">
                <span>or</span>
              </div>

              <div className="join-section">
                <label htmlFor="join-code-input">Join with code</label>
                <div className="join-input-group">
                  <input
                    type="text"
                    placeholder="ABC123"
                    defaultValue={joinCode || ''}
                    id="join-code-input"
                    className="join-code-input"
                    maxLength={6}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('join-code-input') as HTMLInputElement
                      handleJoinNetworkGame(input.value.toUpperCase())
                    }}
                    disabled={loading || !playerName.trim()}
                    className="primary-button join-btn"
                  >
                    {loading ? 'Joining...' : 'Join'}
                  </button>
                </div>
              </div>
            </div>

            {error && <div className="error-message">⚠️ {error}</div>}
          </div>
        </div>
      </div>
    )
  }

  // Network waiting (arcade-style lobby)
  if (gameMode === 'network' && phase === 'waiting') {
    return (
      <div className="tictactoe-container">
        <NotificationComponent />

        {/* Standardized Header */}
        <GameHeader
          gameTitle="Ultimate Tic-Tac-Toe"
          gameIcon="⭕"
          players={[
            { number: 1, name: session?.player1_name || 'Player 1', isConnected: true, avatar: 'X' },
            { number: 2, name: 'Waiting...', isConnected: false, avatar: '?' }
          ]}
          sessionCode={session?.session_code}
        />

        {/* Arcade-style lobby screen */}
        <div className="arcade-lobby-screen">
          <div className="arcade-lobby-content">
            <div className="arcade-lobby-banner">
              <h2>Waiting for Player 2...</h2>
              <p className="session-code-display">
                Share code: <strong className="session-code-big">{session?.session_code}</strong>
              </p>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/tictactoe?join=${session?.session_code}`
                  navigator.clipboard.writeText(url).then(() => {
                    showNotification('Join URL copied!', 'success')
                  }).catch(() => {
                    showNotification('Failed to copy URL', 'error')
                  })
                }}
                className="btn btn-secondary"
              >
                📋 Copy Join URL
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Playing
  if (phase === 'playing') {
    const currentPlayerName = gameMode === 'ai'
      ? (currentPlayer === 'X' ? player1Name : 'AI')
      : gameMode === 'local'
      ? (currentPlayer === 'X' ? player1Name : player2Name)
      : (currentPlayer === mySymbol ? playerName : (playerNumber === 1 ? session?.player2_name : session?.player1_name))
    const isMyTurn = gameMode === 'ai'
      ? currentPlayer === 'X'
      : gameMode === 'local' || currentPlayer === mySymbol

    return (
      <div className={`tictactoe-container ${gameMode === 'network' ? 'universal-game-room' : ''}`}>
        <NotificationComponent />

        {/* Standardized Header */}
        <GameHeader
          gameTitle="Ultimate Tic-Tac-Toe"
          gameIcon="⭕"
          players={
            gameMode === 'network'
              ? [
                  { number: 1, name: session?.player1_name || 'Player 1', isConnected: true, avatar: 'X' },
                  { number: 2, name: session?.player2_name || 'Player 2', isConnected: !!session?.player2_name, avatar: 'O' }
                ]
              : gameMode === 'ai'
              ? [
                  { number: 1, name: player1Name, isConnected: true, avatar: 'X' },
                  { number: 2, name: 'AI', isConnected: true, avatar: 'O' }
                ]
              : [
                  { number: 1, name: player1Name, isConnected: true, avatar: 'X' },
                  { number: 2, name: player2Name, isConnected: true, avatar: 'O' }
                ]
          }
          sessionCode={gameMode === 'network' ? session?.session_code : undefined}
          voiceControls={gameMode === 'network' ? {
            connectionStatus: voice.connectionStatus,
            voiceStatus: voice.voiceStatus,
            onToggleMute: voice.toggleMute,
            onConnect: voice.connect,
            onDisconnect: voice.disconnect,
          } : undefined}
        />

        {/* Game Context - Turn & Board Info */}
        <GameContext
          turn={`${currentPlayer} ${currentPlayerName}'s Turn`}
          message={
            nextBoard !== null
              ? `Must play in board ${nextBoard + 1}`
              : gameMode === 'network' && voice.isConnected
              ? '🟢 Real-time'
              : undefined
          }
          urgency={isMyTurn ? 'attention' : 'normal'}
        />

        {/* Main content area with game board and sidebar (network mode only) */}
        {gameMode === 'network' ? (
          <div className="game-room-content">
            {/* Game Board */}
            <div className="game-board-area">
              <div className="ultimate-board">
                {Array(9).fill(null).map((_, i) => renderMiniBoard(i))}
              </div>
            </div>

            {/* Universal Sidebar for network mode */}
            <GameRoomSidebar
              mode="network"
              gameState={{
                turn: `${currentPlayer} ${currentPlayerName}'s Turn`,
                phase: 'Playing'
              }}
              messages={chatMessages}
              historyItems={historyItems}
              onSendMessage={handleSidebarMessage}
              collapsed={sidebar.collapsed}
              onToggleCollapse={sidebar.toggle}
              position={sidebar.position}
              quickReactions={true}
            />
          </div>
        ) : (
          <div className="ultimate-board">
            {Array(9).fill(null).map((_, i) => renderMiniBoard(i))}
          </div>
        )}
      </div>
    )
  }

  // Game finished
  if (phase === 'finished') {
    const winnerName = gameMode === 'ai'
      ? (winner === 'X' ? player1Name : 'AI')
      : gameMode === 'local'
      ? (winner === 'X' ? player1Name : player2Name)
      : (winner === mySymbol ? 'You' : (playerNumber === 1 ? session?.player2_name : session?.player1_name))

    return (
      <div className="tictactoe-container">
        <Link to="/" className="back-button">← Back to Games</Link>
        <NotificationComponent />
        <div className="game-setup">
          <h1>Game Over!</h1>
          <div className={`winner-display ${winner?.toLowerCase()}`}>
            <span className="winner-symbol">{winner}</span>
            <p className="winner-text">{winnerName} wins!</p>
          </div>
          <button onClick={() => {
            if (gameMode === 'local' || gameMode === 'ai') {
              resetGame()
              setPhase('playing')
            } else {
              setPhase('setup')
              setGameMode('select')
            }
          }} className="start-button">
            {gameMode === 'local' || gameMode === 'ai' ? 'Play Again' : 'Back to Menu'}
          </button>
        </div>
      </div>
    )
  }

  return null
}
